import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, InputAdornment, Stack, TextField, Typography,
  Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { TenantVM, fetchTenants, createTenant } from "../data/api";

const statusColor = (s?: string): "success" | "warning" | "default" | "error" => {
  switch ((s || "").toUpperCase()) {
    case "ACTIVE": return "success";
    case "TRIAL": return "warning";
    case "SUSPENDED": return "error";
    case "DISABLED": return "default";
    default: return "default";
  }
};

/** Centralized tenant/distributor management (Phase D). */
const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<TenantVM[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", contactEmail: "", primaryContact: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await fetchTenants()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((t) =>
      [t.name, t.slug, t.contactEmail, t.primaryContact].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))) : rows;
  }, [rows, q]);

  const create = async () => {
    if (!form.name.trim()) { setErr("A company name is required."); return; }
    setSaving(true); setErr(null);
    try {
      await createTenant({ name: form.name, slug: form.slug || undefined,
        contactEmail: form.contactEmail || undefined, primaryContact: form.primaryContact || undefined });
      setAddOpen(false); setForm({ name: "", slug: "", contactEmail: "", primaryContact: "" });
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || "Could not create tenant.");
    } finally { setSaving(false); }
  };

  const totals = useMemo(() => ({
    tenants: rows.length,
    active: rows.filter((t) => (t.status || "").toUpperCase() === "ACTIVE").length,
    buyers: rows.reduce((s, t) => s + (t.buyerCount || 0), 0),
    orders: rows.reduce((s, t) => s + (t.orderCount || 0), 0),
  }), [rows]);

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4">Distributors / Tenants</Typography>
          <Typography variant="body2" color="text.secondary">Select a tenant to inspect its overview, customers, orders, products and staff.</Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setAddOpen(true)}>New tenant</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[["Tenants", totals.tenants], ["Active", totals.active], ["Buyers", totals.buyers], ["Orders", totals.orders]].map(([label, val]) => (
          <Grid size={{ xs: 6, md: 3 }} key={label as string}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{val}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Card><CardContent>
        <TextField size="small" placeholder="Search by name, slug, contact or email" value={q}
          onChange={(e) => setQ(e.target.value)} sx={{ mb: 2, minWidth: { xs: "100%", sm: 320 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>No tenants found.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Company", "Slug", "Status", "Buyers", "Products", "Orders", ""].map((h) => (
                    <TableCell key={h} sx={{ color: "text.secondary", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/admin/tenants/${t.id}`)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                      {t.contactEmail && <Typography variant="caption" color="text.secondary">{t.contactEmail}</Typography>}
                    </TableCell>
                    <TableCell>{t.slug || "—"}</TableCell>
                    <TableCell><Chip size="small" label={t.status} color={statusColor(t.status)} /></TableCell>
                    <TableCell>{t.buyerCount ?? 0}</TableCell>
                    <TableCell>{t.productCount ?? 0}</TableCell>
                    <TableCell>{t.orderCount ?? 0}</TableCell>
                    <TableCell align="right"><Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/admin/tenants/${t.id}`); }}>Inspect</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent></Card>

      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New tenant</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Company name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" required />
            <TextField label="Slug (optional)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} fullWidth size="small" helperText="URL key, e.g. abc-foods" />
            <TextField label="Primary contact" value={form.primaryContact} onChange={(e) => setForm({ ...form, primaryContact: e.target.value })} fullWidth size="small" />
            <TextField label="Contact email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} fullWidth size="small" />
            {err && <Typography variant="caption" color="error">{err}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving}>{saving ? <CircularProgress size={18} /> : "Create"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantsPage;
